<template>
	<!-- Skip Links for Keyboard Navigation — teleported before v-main for first-focus order -->
	<Teleport to="#skip-links-portal">
		<a href="#main-content" class="skip-link" @click.prevent="() => { const el = document.getElementById('main-content'); if (el) el.focus(); }">Skip to main content</a>
		<a href="#summary" class="skip-link" @click.prevent="() => { const el = document.getElementById('summary'); if (el) el.focus(); }">Skip to rewards calculator summary</a>
		<a href="#calculator-tables" class="skip-link" @click.prevent="() => { const el = document.getElementById('calculator-tables'); if (el) el.focus(); }">Skip to calculator tables</a>
	</Teleport>
	
    <div class="bg-color-granite pb-10" role="main" aria-label="Rewards Calculator" id="main-content">
		<header class="d-flex justify-center align-center w-100 position-relative" role="banner" aria-label="Rewards Calculator page header">
			<v-img class="w-100 h-100"			
				:height="199"
				aspect-ratio="16/9"
				src="https://dellpartnerincentives.com/siteassets/Images/HomePartner/Banner/bgRISE2.jpg"
				alt="RISE Rewards Calculator banner"
			></v-img>
			<div class="position-absolute page-header">
				<h1><LanguagePlaceholder default-text="Rewards Calculator" /></h1>
			</div>			
		</header>
        <div class="bg-color-granite w-100 h-100">
            <section class="bg-color-quartz w-100 h-100 my-2" role="region" aria-labelledby="calculator-intro">
                <v-row no-gutters class="d-flex justify-center w-100 pa-0 pt-3 bg-color-dell-blue tx-color-white">
                    <v-col cols="10" class="d-flex flex-column justify-start w-100 pa-0 py-8 tx-font-normal tx-font-size20">  
                        <v-row class="mb-2">
                            <p id="calculator-intro" class="sr-only">Calculator introduction</p>
                            <LanguagePlaceholder is-html
                                default-text="Calculate how much you could earn this incentive period. Simply enter the number of units you intend to purchase below and see how many $ (USD) rewards they will earn you." />
                        </v-row>             
                    </v-col>
                </v-row>
            </section>
            <section class="bg-color-granite w-auto h-100 my-2 mx-10" id="summary" role="region" aria-labelledby="summary-heading">
                <v-row no-gutters class="d-flex justify-center w-100 pa-0">
                    <v-col cols="1" class="bg-color-quartz"></v-col>
                    <v-col cols="10" class="d-flex justify-start w-100 pa-0 pt-10 pb-5 ps-10 tx-font-bold bg-color-quartz">  
                        <h2 id="summary-heading">
                            <LanguagePlaceholder default-text='Rewards Calculator Summary'></LanguagePlaceholder>
                        </h2>   
                    </v-col>
                    <v-col cols="1" class="bg-color-quartz"></v-col>
                    </v-row>
                    <v-row no-gutters class="d-flex justify-center w-100 pa-0 bg-color-quartz">
                    <v-col cols="1" class="bg-color-quartz"></v-col>
                    <v-col cols="10" class="bg-color-white d-flex justify-center flex-column w-100 pa-0 my-5 tx-font-bold tx-font-size14">  
                        <table class="ma-5 px-5 summary-table" aria-label="Rewards Calculator Summary Table" style="width: 100%; border-collapse: collapse;">
                            <caption class="sr-only">Summary of calculated rewards by product category</caption>
                            <thead>
                                <tr style="height: 50px;">
                                    <th scope="col" class="bg-color-cobalt tx-color-glacier summary-cell"></th>
                                    <th scope="col" class="bg-color-cobalt tx-color-glacier summary-cell"><LanguagePlaceholder default-text="PCs+ Displays+Peripherals" /></th>
                                    <th scope="col" class="bg-color-cobalt tx-color-glacier summary-cell"><LanguagePlaceholder default-text="SERVER+" /></th>
                                    <th scope="col" class="bg-color-cobalt tx-color-glacier summary-cell"><LanguagePlaceholder default-text="STORAGE+" /></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr style="height: 50px;">
                                    <th scope="row" class="bg-color-cobalt tx-color-glacier summary-cell"><LanguagePlaceholder default-text="Units" /></th>
                                    <td class="bg-color-glacier tx-color-navy summary-cell">{{ pdpUnits }}</td>
                                    <td class="bg-color-mist tx-color-navy summary-cell">{{ serverUnits }}</td>
                                    <td class="bg-color-sky tx-color-navy summary-cell">{{ storageUnits }}</td>
                                </tr>
                                <tr style="height: 50px;">
                                    <th scope="row" class="bg-color-cobalt tx-color-glacier summary-cell"><LanguagePlaceholder default-text="Units Eligible for Multiplier" /></th>
                                    <td class="bg-color-glacier tx-color-navy summary-cell">{{ pdpMultiplierUnits }}</td>
                                    <td class="bg-color-mist tx-color-navy summary-cell">{{ serverMultiplierUnits }}</td>
                                    <td class="bg-color-sky tx-color-navy summary-cell">{{ storageMultiplierUnits }}</td>
                                </tr>
                                <tr style="height: 50px;">
                                    <th scope="row" class="bg-color-cobalt tx-color-glacier summary-cell"><LanguagePlaceholder default-text="Multiplier Threshold Achieved" /></th>
                                    <td class="bg-color-glacier tx-color-navy summary-cell">{{ pdpmultiplier }}x Rewards</td>
                                    <td class="bg-color-mist tx-color-navy summary-cell">{{ serverMultiplier }}x Rewards</td>
                                    <td class="bg-color-sky tx-color-navy summary-cell">{{ storageMultiplier }}x Rewards</td>
                                </tr>
                                <tr style="height: 50px;">
                                    <th scope="row" class="bg-color-cobalt tx-color-glacier summary-cell"><LanguagePlaceholder default-text="Base Reward Earned" /></th>
                                    <td class="bg-color-glacier tx-color-navy summary-cell">{{ $filters.currencyUSD(pdpUsdReward) }}</td>
                                    <td class="bg-color-mist tx-color-navy summary-cell">{{ $filters.currencyUSD(serverUsdReward) }}</td>
                                    <td class="bg-color-sky tx-color-navy summary-cell">{{ $filters.currencyUSD(storageUsdReward) }}</td>
                                </tr>
                                <tr style="height: 50px;">
                                    <th scope="row" class="bg-color-cobalt tx-color-glacier summary-cell"><LanguagePlaceholder default-text="Multiplier Reward Earned" /></th>
                                    <td class="bg-color-glacier tx-color-navy summary-cell">{{ $filters.currencyUSD(pdpUsdRewardWithMultiplier) }}</td>
                                    <td class="bg-color-mist tx-color-navy summary-cell">{{ $filters.currencyUSD(serverUsdRewardWithMultiplier) }}</td>
                                    <td class="bg-color-sky tx-color-navy summary-cell">{{ $filters.currencyUSD(storageUsdRewardWithMultiplier) }}</td>
                                </tr>
                                <tr style="height: 50px;">
                                    <th scope="row" id="grand-total-earned-label" class="bg-color-cobalt tx-color-glacier summary-cell"><LanguagePlaceholder default-text="Grand Total" /></th>
                                    <td class="grand-total-earned-value tx-color-navy summary-cell" headers="grand-total-earned-label">
                                        {{ $filters.currencyUSD(totalUsdReward) }}
                                        <span class="sr-only">Includes category totals with multipliers plus rewards that do not receive a multiplier.</span>
                                    </td>
                                    <td colspan="2" class="bg-color-cobalt tx-color-white summary-cell">
                                        <LanguagePlaceholder is-html default-text="Please note: Displays and Client Peripherals do not count towards the multiplier thresholds." />
                                    </td>
                                </tr>
                                <tr style="height: 50px;">
                                    <th scope="row" class="bg-color-cobalt summary-cell" aria-hidden="true"></th>
                                    <td class="bg-color-glacier summary-cell" aria-hidden="true"></td>
                                    <td colspan="2" class="bg-color-cobalt tx-color-navy summary-cell">
                                        <v-btn @click="downloadSummary" variant="flat" class="mx-2" color="#0C32A4"
                                            aria-label="Download rewards calculator summary as PNG image"
                                            @keydown.enter="downloadSummary"
                                            @keydown.space.prevent="downloadSummary"
                                            tabindex="0">
                                            <LanguagePlaceholder default-text="Download Summary" />
                                        </v-btn>
                                    </td>
                                </tr>
                            </tbody>
                            <p class="font-italic font-weight-regular mb-4" role="note">
                                        <LanguagePlaceholder default-text="All rewards shown are earned and paid in USD ($)." />
                                    </p>
                        </table>
                        
                        <section class="d-flex flex-column ma-5" id="calculator-tables" role="region" aria-labelledby="calculator-tables-heading">
                            <h3 id="calculator-tables-heading" class="sr-only">Product Calculator Tables</h3>
                            <v-card class="elevation-0">
                                <template v-slot:text>
                                    <v-text-field v-if="screenshot === false" clearable v-model="searchProduct" 
                                        prepend-inner-icon="mdi-magnify" variant="outlined" hide-details single-line
                                        label="Search products"
                                        aria-describedby="search-description"
                                        @keydown.enter.prevent
                                        @keydown.escape="searchProduct = ''"
                                        tabindex="0">
                                        <template v-slot:label>
                                            <LanguagePlaceholder default-text="Search products" />
                                        </template>
                                    </v-text-field>
                                    <span id="search-description" class="sr-only">Use this field to search and filter products in the rewards calculator tables below. Press Enter to search, Escape to clear.</span>
                                    <span aria-live="polite" aria-atomic="true" class="sr-only">{{ searchStatusMessage }}</span>
                                </template>
                                <div class="pa-4">
                                    <v-expansion-panels v-model="panel" tile variant="accordion" multiple>
                                        <v-expansion-panel>
                                
                                            <v-expansion-panel-title color="#3F8FD8" class="text-white">
                                                <LanguagePlaceholder default-text="PCS+ DISPLAYS+PERIPHERALS" />
                                            </v-expansion-panel-title>
                                    

                                            <v-expansion-panel-text class="no-padding innerExPan">
                                                <v-data-table :items-per-page="-1" hide-default-footer :headers="headers" class="v-data-table-row"
                                                    :items="displayItemsPC" item-key="id" :search="searchProduct"
                                                    role="table" aria-label="PC, Displays and Peripherals rewards calculator">
                                                    <template v-slot:item.estimatedunits="{ item }">
                                                        <v-text-field @blur="calculateRewards" type="number" hide-details
                                                            title ="Estimated Units"
                                                            aria-label ="Estimated Units"
                                                            v-model="item.estimatedUnits" variant="outlined" class="my-1">

                                                        </v-text-field>
                                                    </template>
                                                    <template v-slot:item.counttowardsmultiplier="{ item }">
                                                        {{ item.countTowardsMultiplier === true ? 'Yes' : 'No' }}
                                                    </template>
                                                    <template v-slot:item.rewardsperbox="{ item }">
                                                        {{ $filters.currencyUSD(item.rewardsPerBox) }}
                                                    </template>
                                                </v-data-table>
                                            </v-expansion-panel-text>
                                        </v-expansion-panel>

                                        <v-expansion-panel>
                                
                                            <v-expansion-panel-title color="#3F8FD8" class="text-white">
                                                <LanguagePlaceholder default-text="SERVER+" />
                                            </v-expansion-panel-title>
                                    

                                            <v-expansion-panel-text class="no-padding innerExPan">
                                                <v-data-table :items-per-page="-1" hide-default-footer :headers="headers" class="v-data-table-row"
                                                    :items="displayItemsSERVER" item-key="id" :search="searchProduct"
                                                    role="table" aria-label="Server rewards calculator">
                                                    <template v-slot:item.estimatedunits="{ item }">
                                                        <v-text-field @blur="calculateRewards" type="number" hide-details
                                                            title ="Estimated Units"
                                                            aria-label ="Estimated Units"
                                                            v-model="item.estimatedUnits" variant="outlined" class="my-1">

                                                        </v-text-field>
                                                    </template>
                                                    <template v-slot:item.counttowardsmultiplier="{ item }">
                                                        {{ item.countTowardsMultiplier === true ? 'Yes' : 'No' }}
                                                    </template>
                                                    <template v-slot:item.rewardsperbox="{ item }">
                                                        {{ $filters.currencyUSD(item.rewardsPerBox) }}
                                                    </template>
                                                </v-data-table>
                                            </v-expansion-panel-text>
                                        </v-expansion-panel>

                                        <v-expansion-panel>
                                
                                            <v-expansion-panel-title color="#3F8FD8" class="text-white">
                                                <LanguagePlaceholder default-text="STORAGE+" />
                                            </v-expansion-panel-title>
                                    

                                            <v-expansion-panel-text class="no-padding innerExPan">
                                                <v-data-table :items-per-page="-1" hide-default-footer :headers="headers" class="v-data-table-row"
                                                    :items="displayItemsSTORAGE" item-key="id" :search="searchProduct"
                                                    role="table" aria-label="Storage rewards calculator">
                                                    <template v-slot:item.estimatedunits="{ item }">
                                                        <v-text-field @blur="calculateRewards" type="number" hide-details
                                                            title ="Estimated Units"
                                                            aria-label ="Estimated Units"
                                                            v-model="item.estimatedUnits" variant="outlined" class="my-1">

                                                        </v-text-field>
                                                    </template>
                                                    <template v-slot:item.counttowardsmultiplier="{ item }">
                                                        {{ item.countTowardsMultiplier === true ? 'Yes' : 'No' }}
                                                    </template>
                                                    <template v-slot:item.rewardsperbox="{ item }">
                                                        {{ $filters.currencyUSD(item.rewardsPerBox) }}
                                                    </template>
                                                </v-data-table>
                                            </v-expansion-panel-text>
                                        </v-expansion-panel>

                                    </v-expansion-panels>
                                </div>

                            </v-card>
                        </section>
                    </v-col>
                    <v-col cols="1" class="bg-color-quartz"></v-col>
                </v-row>
            </section>
        </div>
    </div>

</template>

<script setup>
import { ref, onMounted, computed, nextTick, watch } from 'vue'
import LanguagePlaceholder from '@/components/LanguagePlaceholder.vue'
import imgUrl_rewardsCalculator from '@/assets/img/banner/green_rewardscalculator.jpg'
import html2canvas from 'html2canvas';
import { useSystemStore } from '@/stores/system'


const systemStore = useSystemStore()

const headers = [
    { 
        title: 'Line of Business', 
        key: 'section', 
        value: 'section', 
        sortable: true,
        headerProps: { class: 'v-data-table-header', 'aria-label': 'Line of Business column header, press Enter or Space to sort' } 
    },
    { 
        title: 'Product', 
        key: 'productDescription', 
        value: 'productDescription', 
        sortable: true,
        headerProps: { class: 'v-data-table-header', 'aria-label': 'Product column header, press Enter or Space to sort' } 
    },
    { 
        title: 'Counts Towards Rewards Multiplier', 
        value: 'counttowardsmultiplier', 
        sortable: true,
        headerProps: { class: 'v-data-table-header', 'aria-label': 'Counts Towards Rewards Multiplier column header, press Enter or Space to sort' } 
    },
    { 
        title: 'Estimated Units', 
        value: 'estimatedunits', 
        sortable: false,
        headerProps: { class: 'v-data-table-header', 'aria-label': 'Estimated Units column header' } 
    },
    { 
        title: '$ (USD) Per Unit', 
        value: 'rewardsperbox', 
        sortable: true,
        headerProps: { class: 'v-data-table-header', 'aria-label': 'USD Per Unit column header, press Enter or Space to sort' } 
    },
]

// Make table headers keyboard accessible
const makeHeadersKeyboardAccessible = () => {
    nextTick(() => {
        const tables = document.querySelectorAll('.v-data-table')
        tables.forEach(table => {
            const headerElements = table.querySelectorAll('thead th')
            headerElements.forEach((header, index) => {
                // Try to match header by index first, then by text content
                let headerConfig = headers[index]
                if (!headerConfig) {
                    // Fallback: try to match by text content
                    const headerText = header.textContent?.trim()
                    headerConfig = headers.find(h => h.title === headerText || headerText.includes(h.title))
                }
                
                // Check if header has sort icon (Vuetify adds this for sortable columns)
                const hasSortIcon = header.querySelector('.v-data-table-header__sort, .v-data-table-header__icon, [class*="sort"]')
                const isSortable = headerConfig?.sortable || hasSortIcon !== null
                
                if (headerConfig) {
                    if (isSortable) {
                        // Make sortable header focusable
                        header.setAttribute('tabindex', '0')
                        header.setAttribute('role', 'columnheader button')
                        const ariaLabel = headerConfig.headerProps?.['aria-label'] || `${headerConfig.title} column header, press Enter or Space to sort`
                        header.setAttribute('aria-label', ariaLabel)
                        
                        // Store the handler reference for cleanup
                        if (!header._keyboardHandler) {
                            header._keyboardHandler = (e) => {
                                const key = e.key
                                const row = header.closest('tr')
                                const focusables = row ? Array.from(row.querySelectorAll('th[tabindex="0"]')) : []
                                const currentIndex = focusables.indexOf(header)

                                const moveFocus = (nextIndex) => {
                                    if (!focusables.length) return
                                    const target = focusables[Math.max(0, Math.min(nextIndex, focusables.length - 1))]
                                    target?.focus?.()
                                }

                                if (key === 'ArrowLeft') {
                                    e.preventDefault()
                                    moveFocus(currentIndex - 1)
                                    return
                                }

                                if (key === 'ArrowRight') {
                                    e.preventDefault()
                                    moveFocus(currentIndex + 1)
                                    return
                                }

                                if (key === 'Home') {
                                    e.preventDefault()
                                    moveFocus(0)
                                    return
                                }

                                if (key === 'End') {
                                    e.preventDefault()
                                    moveFocus(focusables.length - 1)
                                    return
                                }

                                if (key === 'Enter' || key === ' ') {
                                    e.preventDefault()
                                    // Trigger click to sort (Vuetify handles the sorting)
                                    const sortButton = header.querySelector('.v-data-table-header__sort, .v-data-table-header__icon, [class*="sort"]')
                                    if (sortButton) {
                                        sortButton.click()
                                    } else {
                                        // If no sort button, click the header itself
                                        header.click()
                                    }
                                }
                            }
                            header.addEventListener('keydown', header._keyboardHandler)
                        }
                    } else {
                        // Non-sortable headers still need proper role
                        header.setAttribute('role', 'columnheader')
                        header.setAttribute('tabindex', '-1')
                        if (headerConfig.headerProps?.['aria-label']) {
                            header.setAttribute('aria-label', headerConfig.headerProps['aria-label'])
                        }
                    }
                } else if (hasSortIcon) {
                    // Fallback: if we can't match config but header has sort icon, make it accessible
                    header.setAttribute('tabindex', '0')
                    header.setAttribute('role', 'columnheader button')
                    const headerText = header.textContent?.trim() || 'Column'
                    header.setAttribute('aria-label', `${headerText} column header, press Enter or Space to sort`)
                    
                    if (!header._keyboardHandler) {
                        header._keyboardHandler = (e) => {
                            const key = e.key
                            const row = header.closest('tr')
                            const focusables = row ? Array.from(row.querySelectorAll('th[tabindex="0"]')) : []
                            const currentIndex = focusables.indexOf(header)

                            const moveFocus = (nextIndex) => {
                                if (!focusables.length) return
                                const target = focusables[Math.max(0, Math.min(nextIndex, focusables.length - 1))]
                                target?.focus?.()
                            }

                            if (key === 'ArrowLeft') {
                                e.preventDefault()
                                moveFocus(currentIndex - 1)
                                return
                            }

                            if (key === 'ArrowRight') {
                                e.preventDefault()
                                moveFocus(currentIndex + 1)
                                return
                            }

                            if (key === 'Home') {
                                e.preventDefault()
                                moveFocus(0)
                                return
                            }

                            if (key === 'End') {
                                e.preventDefault()
                                moveFocus(focusables.length - 1)
                                return
                            }

                            if (key === 'Enter' || key === ' ') {
                                e.preventDefault()
                                const sortButton = header.querySelector('.v-data-table-header__sort, .v-data-table-header__icon, [class*="sort"]')
                                if (sortButton) {
                                    sortButton.click()
                                } else {
                                    header.click()
                                }
                            }
                        }
                        header.addEventListener('keydown', header._keyboardHandler)
                    }
                }
            })
        })
    })
}

const eligibleProducts = ref({})
const searchProduct = ref('')

const searchStatusMessage = computed(() => {
    if (!searchProduct.value) return ''
    const query = searchProduct.value.toLowerCase()
    const matchesSearch = (item) =>
        Object.values(item).some(val => String(val ?? '').toLowerCase().includes(query))
    const countFiltered = (items) => (items ?? []).filter(matchesSearch).length
    const total = countFiltered(displayItemsPC.value) + countFiltered(displayItemsSERVER.value) + countFiltered(displayItemsSTORAGE.value)
    return total === 0
        ? `No products found for "${searchProduct.value}"`
        : `${total} product${total !== 1 ? 's' : ''} found for "${searchProduct.value}"`
})
const calculated = ref(false)

const totalUnits = ref(0)
const totalUsdRewards = ref(0)

const pdpUnits = ref(0)
const pdpMultiplierUnits = ref(0)
const pdpmultiplier = ref(0)
const pdpUsdReward = ref(0)
const pdpUsdRewardWithMultiplier = ref(0)

const serverUnits = ref(0)
const serverMultiplierUnits = ref(0)
const serverMultiplier = ref(0)
const serverUsdReward = ref(0)
const serverUsdRewardWithMultiplier = ref(0)

const storageUnits = ref(0)
const storageMultiplierUnits = ref(0)
const storageMultiplier = ref(0)
const storageUsdReward = ref(0)
const storageUsdRewardWithMultiplier = ref(0)


const totalUsdReward = ref(0)
const screenshot = ref(false)
const panel = ref(0)

const displayItems = computed(() => {
    if (screenshot.value === true) {
        if (eligibleProducts.value.rewardsCalculatorProducts != undefined) {
            return eligibleProducts.value.rewardsCalculatorProducts.filter(product => {
                return Number(product.estimatedUnits) > 0
            })
        } else {
            return eligibleProducts.value.rewardsCalculatorProducts
        }

    } else {
        return eligibleProducts.value.rewardsCalculatorProducts
    }

})

const displayItemsPC = computed(() => {
    if (screenshot.value === true) {
        if (eligibleProducts.value.rewardsCalculatorProducts != undefined) {
            return eligibleProducts.value.rewardsCalculatorProducts.filter(product => {
                return Number(product.estimatedUnits) > 0 && product.section === 'PCs + Displays + Peripherals'
            })
        }
    } else {
        if (eligibleProducts.value.rewardsCalculatorProducts != undefined) {
            return eligibleProducts.value.rewardsCalculatorProducts.filter(product => {
                return product.section === 'PCs + Displays + Peripherals'
            })
        }
    }
})

const displayItemsSERVER = computed(() => {
    if (screenshot.value === true) {
        if (eligibleProducts.value.rewardsCalculatorProducts != undefined) {
            return eligibleProducts.value.rewardsCalculatorProducts.filter(product => {
                return Number(product.estimatedUnits) > 0 && product.section === 'Server+'
            })
        }
    } else {
        if (eligibleProducts.value.rewardsCalculatorProducts != undefined) {
            return eligibleProducts.value.rewardsCalculatorProducts.filter(product => {
                return product.section === 'Server+'
            })
        }
    }
})

const displayItemsSTORAGE = computed(() => {
    if (screenshot.value === true) {
        if (eligibleProducts.value.rewardsCalculatorProducts != undefined) {
            return eligibleProducts.value.rewardsCalculatorProducts.filter(product => {
                return Number(product.estimatedUnits) > 0 && product.section === 'Storage+'
            })
        }
    } else {
        if (eligibleProducts.value.rewardsCalculatorProducts != undefined) {
            return eligibleProducts.value.rewardsCalculatorProducts.filter(product => {
                return product.section === 'Storage+'
            })
        }
    }
})

const calculateRewards = () => {
    totalUnits.value = 0
    totalUsdRewards.value = 0
    pdpUnits.value = 0
    pdpMultiplierUnits.value = 0
    pdpmultiplier.value = 1
    pdpUsdReward.value = 0
    pdpUsdRewardWithMultiplier.value = 0

    serverUnits.value = 0
    serverMultiplierUnits.value = 0
    serverMultiplier.value = 1
    serverUsdReward.value = 0
    serverUsdRewardWithMultiplier.value = 0

    storageUnits.value = 0
    storageMultiplierUnits.value = 0
    storageMultiplier.value = 1
    storageUsdReward.value = 0
    storageUsdRewardWithMultiplier.value = 0

    totalUsdReward.value = 0

    let pdpEligibleUsd = 0
    let serverEligibleUsd = 0
    let storageEligibleUsd = 0

    eligibleProducts.value.rewardsCalculatorProducts.forEach((item) => {
        var estUnits = Number(item.estimatedUnits)

        if (estUnits > 0) {
            totalUnits.value = totalUnits.value + estUnits;
            var itemUsd = estUnits * item.rewardsPerBox

            if (item.section === 'PCs + Displays + Peripherals') {
                pdpUnits.value = pdpUnits.value + estUnits
                pdpUsdReward.value = pdpUsdReward.value + itemUsd

                if (item.countTowardsMultiplier === true) {
                    pdpMultiplierUnits.value = pdpMultiplierUnits.value + estUnits
                    pdpEligibleUsd = pdpEligibleUsd + itemUsd
                }
            }

            if (item.section === 'Server+') {
                serverUnits.value = serverUnits.value + estUnits
                serverUsdReward.value = serverUsdReward.value + itemUsd

                if (item.countTowardsMultiplier === true) {
                    serverMultiplierUnits.value = serverMultiplierUnits.value + estUnits
                    serverEligibleUsd = serverEligibleUsd + itemUsd
                }
            }

            if (item.section === 'Storage+') {
                storageUnits.value = storageUnits.value + estUnits
                storageUsdReward.value = storageUsdReward.value + itemUsd

                if (item.countTowardsMultiplier === true) {
                    storageMultiplierUnits.value = storageMultiplierUnits.value + estUnits
                    storageEligibleUsd = storageEligibleUsd + itemUsd
                }
            }
        }
    })

    // calc multipliers
    // pdp
    if (pdpMultiplierUnits.value >= 30 && pdpMultiplierUnits.value < 80) {
        pdpmultiplier.value = 1.2
    }
    if (pdpMultiplierUnits.value >= 80 && pdpMultiplierUnits.value < 160) {
        pdpmultiplier.value = 1.3
    }
    if (pdpMultiplierUnits.value >= 160 && pdpMultiplierUnits.value < 250) {
        pdpmultiplier.value = 1.4
    }
    if (pdpMultiplierUnits.value >= 250 && pdpMultiplierUnits.value < 400) {
        pdpmultiplier.value = 1.5
    }
    if (pdpMultiplierUnits.value >= 400) {
        pdpmultiplier.value = 1.6
    }

    //server
    if (serverMultiplierUnits.value >= 5 && serverMultiplierUnits.value < 9) {
        serverMultiplier.value = 1.2
    }
    if (serverMultiplierUnits.value >= 9 && serverMultiplierUnits.value < 13) {
        serverMultiplier.value = 1.3
    }
    if (serverMultiplierUnits.value >= 13 && serverMultiplierUnits.value < 17) {
        serverMultiplier.value = 1.4
    }
    if (serverMultiplierUnits.value >= 17 && serverMultiplierUnits.value < 21) {
        serverMultiplier.value = 1.5
    }
    if (serverMultiplierUnits.value >= 21) {
        serverMultiplier.value = 1.6
    }

    //storage
    if (storageMultiplierUnits.value >= 2 && storageMultiplierUnits.value < 5) {
        storageMultiplier.value = 1.2
    }
    if (storageMultiplierUnits.value >= 5 && storageMultiplierUnits.value < 8) {
        storageMultiplier.value = 1.3
    }
    if (storageMultiplierUnits.value >= 8 && storageMultiplierUnits.value < 11) {
        storageMultiplier.value = 1.4
    }
    if (storageMultiplierUnits.value >= 11 && storageMultiplierUnits.value < 13) {
        storageMultiplier.value = 1.5
    }
    if (storageMultiplierUnits.value >= 13) {
        storageMultiplier.value = 1.6
    }



    pdpUsdRewardWithMultiplier.value = pdpEligibleUsd * pdpmultiplier.value
    serverUsdRewardWithMultiplier.value = serverEligibleUsd * serverMultiplier.value
    storageUsdRewardWithMultiplier.value = storageEligibleUsd * storageMultiplier.value

    const pdpUsdRewardWithoutMultiplier = pdpUsdReward.value - pdpEligibleUsd
    const serverUsdRewardWithoutMultiplier = serverUsdReward.value - serverEligibleUsd
    const storageUsdRewardWithoutMultiplier = storageUsdReward.value - storageEligibleUsd

    totalUsdReward.value = pdpUsdRewardWithMultiplier.value
        + serverUsdRewardWithMultiplier.value
        + storageUsdRewardWithMultiplier.value
        + pdpUsdRewardWithoutMultiplier
        + serverUsdRewardWithoutMultiplier
        + storageUsdRewardWithoutMultiplier
}

const downloadSummary = () => {
    systemStore.showLoading(true)
    var srh = searchProduct.value
    searchProduct.value = ''
    screenshot.value = true
    setTimeout(() => {
        html2canvas(document.querySelector("#summary")).then(function (canvas) {
            systemStore.showLoading(false)
            var dataURL = canvas.toDataURL();
            var timestamp = Number(new Date());
            systemStore.downloadFile(dataURL, 'Rewards_Calculator_Summary_' + timestamp + '.png')

            screenshot.value = false
            searchProduct.value = srh
        });
    }, 100);

}

onMounted(() => {


    systemStore.getEligibleProducts().then(result => {
        if (result.success === true) {
            eligibleProducts.value = result
            // Make headers keyboard accessible after data loads
            makeHeadersKeyboardAccessible()
        } else {
            systemStore.showMessage('Error retrieving data, please try again or contact support for assistance.', { color: 'error' })
        }
    }).catch(error => {
        systemStore.showMessage('Error retrieving data, please try again or contact support for assistance.', { color: 'error' })
    })
    
    // Also make headers accessible when expansion panels open
    setTimeout(() => {
        makeHeadersKeyboardAccessible()
    }, 500)
})

// Watch for expansion panel changes to make headers accessible
watch(panel, () => {
    setTimeout(() => {
        makeHeadersKeyboardAccessible()
    }, 300)
})



const calculate = () => {
    calculated.value = true
}

</script>

<style scoped>

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Make table headers keyboard accessible with visible focus */
:deep(.v-data-table thead th[tabindex="0"]) {
  cursor: pointer;
  outline: none;
}

/* Table header focus — #003785 vs white ~13:1, meets 3:1 */
:deep(.v-data-table thead th[tabindex="0"]:focus-visible) {
  outline: 3px solid #003785 !important;
  outline-offset: 2px !important;
  background-color: rgba(0, 55, 133, 0.08) !important;
  color: #003785 !important;
  position: relative !important;
  z-index: 10 !important;
}

:deep(.v-data-table thead th[tabindex="0"]:focus-visible *) {
  color: #003785 !important;
}

:deep(.v-data-table thead th[role="columnheader button"]:hover) {
  background-color: rgba(88, 165, 230, 0.2) !important;
}

/* Search field focus indicator */
:deep(.v-text-field .v-field:focus-within) {
  outline: 3px solid #003785 !important;
  outline-offset: 2px !important;
}

/* Accordion / expansion-panel title focus */
:deep(.v-expansion-panel-title:focus-visible) {
  outline: 3px solid #003785 !important;
  outline-offset: 2px !important;
}

/* Buttons */
:deep(.v-btn:focus-visible) {
  outline: 3px solid #003785 !important;
  outline-offset: 3px !important;
  position: relative !important;
  z-index: 10 !important;
}

.summary-table {
  table-layout: fixed;
}

.summary-cell {
  padding: 8px 12px;
  vertical-align: middle;
  font-weight: inherit;
}

.summary-table th[scope="row"] {
  text-align: left;
}

/* Glacier #E6F8FF is nearly white, so a 10% black mix stays pale. This step is visibly darker. Navy #00227F stays above 4.5:1. */
.grand-total-earned-value {
  background-color: #A3C5D6 !important;
}

/* WCAG 1.4.4 / 1.4.10 — Prevent content overlap and loss at 200%+ zoom / 320px */
@media (max-width: 960px) {
  :deep(.mx-10) {
    margin-left: 0.5rem !important;
    margin-right: 0.5rem !important;
  }
  .summary-table {
    table-layout: auto;
    min-width: 320px;
  }
  :deep(.summary-cell) {
    padding: 4px 6px;
    font-size: 0.85em;
  }
}

@media (max-width: 599px) {
  :deep(.v-data-table) {
    display: block;
    overflow-x: auto;
    width: 100%;
    -webkit-overflow-scrolling: touch;
  }
  :deep(.summary-table) {
    display: block;
    overflow-x: auto;
    width: 100%;
  }
}
</style>